/**
 * Cliente API canónica aarpia-lite.
 *
 * Patrón: cada call usa JWT del store + Idempotency-Key cuando aplica.
 * Doctrina: consume /api/v1/canonico/<sub>/<token>/<estado>/v<v>/... directamente.
 *
 * En dev, vite.config.ts proxy /api/v1 → http://localhost:3000.
 * En prod, mismo origen (arquitecto.aarpia-lite.es/api/v1 → backend mismo droplet).
 */

const API_BASE = '/api/v1'

/**
 * CUI-2 (orden 0eab5681) — dos PROPIEDADES heredables del conector-universal,
 * centralizadas aquí para que NINGUNA página/cliente necesite parche propio:
 *
 *  (a) ROUTING VERSION-AGNÓSTICO: toda ruta /canonico/<sub>/<token>/<estado>/vX.Y
 *      se re-resuelve contra el catálogo VIVO justo antes del fetch (la versión
 *      que el caller escribió es solo semilla). Si aun así el backend responde
 *      404/409 de versión (autobump §13 a mitad de sesión), se recarga el
 *      catálogo y se reintenta UNA vez con la versión fresca. Los autobumps
 *      dejan de romper clientes.
 *
 *  (b) AUTH-READY GATING: las llamadas a /canonico, /mcp, /eventos y /docs
 *      esperan (acotado) a que la sesión esté completa (token + CO activo) y,
 *      las canónicas, a que el catálogo haya cargado — mata el race 401/404 de
 *      arranque cuando un componente dispara fetch antes de hidratar stores.
 *
 * Sin acoplar api.ts a los stores (ciclo de imports): App.tsx registra un
 * proveedor de sesión vía `registrarProveedorSesion`. Si no hay proveedor
 * registrado (tests, herramientas), el comportamiento es el clásico.
 */
export interface SesionParaApi {
  token: string | null
  coId: string | null
  catalogoListo: boolean
  cargarCatalogo: () => Promise<void>
  recargarCatalogo: () => Promise<void>
  versionDe: (sub: string, token: string, estado: string) => string | null
}
let proveedorSesion: (() => SesionParaApi) | null = null
export function registrarProveedorSesion(p: () => SesionParaApi): void {
  proveedorSesion = p
}

const RE_RUTA_CANONICA = /^(\/canonico\/([a-z0-9-]+)\/([a-z0-9-]+)\/([a-z0-9-]+)\/v)(\d+\.\d+)([/?].*)?$/
const RE_RUTA_GATEADA = /^\/(canonico|mcp|eventos|docs)(\/|$|\?)/

function esperar(cond: () => boolean, timeoutMs: number): Promise<boolean> {
  return new Promise((resolve) => {
    if (cond()) return resolve(true)
    const t0 = Date.now()
    const iv = setInterval(() => {
      if (cond()) { clearInterval(iv); resolve(true) }
      else if (Date.now() - t0 > timeoutMs) { clearInterval(iv); resolve(false) }
    }, 50)
  })
}

/** Reescribe la versión de una ruta canónica con la del catálogo vivo (si la conoce). */
function reescribirVersion(path: string, versionDe: SesionParaApi['versionDe']): string {
  const m = path.match(RE_RUTA_CANONICA)
  if (!m) return path
  const viva = versionDe(m[2], m[3], m[4])
  if (!viva || viva === m[5]) return path
  return `${m[1]}${viva}${m[6] || ''}`
}

/**
 * Evento global emitido cuando una llamada autenticada recibe 401 (token
 * expirado/revocado). `App.tsx` lo escucha y llama `logout()` → `ProtectedRoute`
 * redirige a /login. Se emite vía CustomEvent para no acoplar `api.ts` al store
 * (evita el ciclo de imports api ↔ auth). Cumple la intención de auth.ts:8.
 */
export const UNAUTHORIZED_EVENT = 'auth:unauthorized'

/** Trunca un cuerpo de error no-JSON (p.ej. HTML de un 500) para no volcarlo crudo en un toast. */
function safeErrorDetail(text: string): string {
  const trimmed = text.trim()
  if (!trimmed) return ''
  // Si parece HTML/markup, no exponer el cuerpo crudo al usuario.
  if (trimmed.startsWith('<')) return 'El servidor devolvió una respuesta no válida.'
  return trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed
}

export class ApiError extends Error {
  status: number
  title: string
  detail?: string
  errors?: string[]
  raw?: unknown
  constructor(status: number, title: string, detail?: string, errors?: string[], raw?: unknown) {
    super(detail || title || `HTTP ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.title = title
    this.detail = detail
    this.errors = errors
    this.raw = raw
  }
}

interface FetchOptions extends RequestInit {
  idempotencyKey?: string
  /** interno: marca el reintento único tras recargar el catálogo (autobump §13). */
  _reintentoVersion?: boolean
}

export async function apiFetch<T = unknown>(
  path: string,
  token: string | null,
  options: FetchOptions = {},
): Promise<T> {
  // ── CUI-2b: auth-ready gating ──
  if (proveedorSesion && RE_RUTA_GATEADA.test(path)) {
    await esperar(() => {
      const s = proveedorSesion!()
      return !!(s.token && s.coId)
    }, 10000)
    const s = proveedorSesion()
    // El caller pudo capturar token=null antes de hidratar el store: usar el fresco.
    if (!token && s.token) token = s.token
    // ── CUI-2a: versión del catálogo vivo para rutas canónicas ──
    if (RE_RUTA_CANONICA.test(path)) {
      if (!s.catalogoListo) {
        s.cargarCatalogo().catch(() => {})
        await esperar(() => proveedorSesion!().catalogoListo, 8000)
      }
      path = reescribirVersion(path, proveedorSesion().versionDe)
    }
  }

  const headers = new Headers(options.headers)
  headers.set('Content-Type', 'application/json')
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (options.idempotencyKey) headers.set('Idempotency-Key', options.idempotencyKey)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  // Máxima información de entidad en la respuesta: el backend incluye la guía densa
  // (MANIFEST estructurado) en meta._agent_guide del GET de definición. Inocuo en el
  // resto de endpoints (sólo lo procesa el GET canónico de entidad). Visible en
  // DevTools → Red → Response de cada /canonico/...
  if (!headers.has('Prefer')) headers.set('Prefer', 'include=agent-guide.full')

  const url = path.startsWith('http') ? path : `${API_BASE}${path}`
  const res = await fetch(url, { ...options, headers })
  const text = await res.text()
  let body: any = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = { detail: safeErrorDetail(text) }
  }

  if (!res.ok) {
    // ── CUI-2a: self-heal de autobump §13 a mitad de sesión ──
    // 404 "entidad vX.Y no encontrada" o 409 version_mismatch en ruta canónica:
    // recargar el catálogo y reintentar UNA vez con la versión fresca.
    if (
      proveedorSesion &&
      !options._reintentoVersion &&
      (res.status === 404 || res.status === 409) &&
      RE_RUTA_CANONICA.test(path) &&
      /v\d+\.\d+/.test(String(body?.detail || body?.error || ''))
    ) {
      await proveedorSesion().recargarCatalogo().catch(() => {})
      const rutaFresca = reescribirVersion(path, proveedorSesion().versionDe)
      if (rutaFresca !== path) {
        return apiFetch<T>(rutaFresca, token, { ...options, _reintentoVersion: true })
      }
    }
    // 401 en una llamada AUTENTICADA = token expirado/revocado → cerrar sesión.
    // No se dispara en el login (token === null): ahí un 401 es "credenciales malas".
    if (res.status === 401 && token && typeof window !== 'undefined') {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT))
    }
    throw new ApiError(
      res.status,
      body?.title || `HTTP ${res.status}`,
      body?.detail || body?.error,
      body?.errors,
      body,
    )
  }
  return body as T
}

export const api = {
  get: <T = unknown>(path: string, token: string | null) =>
    apiFetch<T>(path, token, { method: 'GET' }),
  post: <T = unknown>(path: string, token: string | null, body?: unknown, idempotencyKey?: string) =>
    apiFetch<T>(path, token, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
      idempotencyKey,
    }),
  patch: <T = unknown>(path: string, token: string | null, body?: unknown) =>
    apiFetch<T>(path, token, { method: 'PATCH', body: JSON.stringify(body) }),
}

export function generateIdempotencyKey(prefix = 'pwa'): string {
  return `${prefix}-${crypto.randomUUID()}`
}
